import React, { useCallback, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { getLocalStorage } from "../shared/utils/localStorage";
import storageConstants from "../shared/constants/storage";
import middleware from "../shared/middleware";
import { updateExpiryFromResultObj, getTokenRefreshHandler } from "../shared/utils/tokenExpiryHelpers";
import { reloadApp, shouldRetryRefresh } from "../shared/utils";
import errorConstants from "../shared/constants/error";
import {
  registerKeepAliveServiceWorker,
  updateKeepAliveServiceWorkerConfig,
  clearKeepAliveServiceWorkerConfig,
  setupManualServiceWorkerKeepAlive,
} from "../shared/utils/serviceWorkerKeepAlive";
import constants from "../shared/constants";

import { setCookie } from "../shared/utils/cookie";
import { setLocalStorage } from "../shared/utils/localStorage";

const { AVS_REFRESH_TOKEN } = storageConstants;
const { APP_ENVIRONMENTS, API_BASE_URL } = constants;
const { AVS_ERROR_CODES } = errorConstants;

/**
 * Session Expiry Manager Component
 *
 * @param {object} appProvider - The application provider object
 * @param {object} userProfile - The user profile object
 * @param {object} videoPlaying - The video playing state
 * @param {number} keepAliveDelay - Delay interval for keep-alive in seconds
 * @param {object} featureToggles - Feature toggles object
 */
function SessionExpiryManager({ appProvider, userProfile, videoPlaying, featureToggles }) {
  const manualSwKeepAliveCleanup = useRef(null);

  const buildRenewUrl = useCallback(() => {
    let url = appProvider?.config?.avs_api_urls?.user_session_renewal;
    if (!url) return null;

    url = url.replace("$TENANT_URL", appProvider.AGL_TENANT_URL);
    url = url.replace("$AGL_VERSION", appProvider.AGL_Version);
    url = url.replace("$LANG", appProvider.AVS_LANG);
    url = url.replace("$PLATFORM", appProvider.AVS_PLATFORM);
    url = url.replace("$PROPERTY", appProvider.AVS_PROPERTY);

    const env = process.env.NODE_ENV || APP_ENVIRONMENTS.PROD;
    if (env === APP_ENVIRONMENTS.DEV) {
      url = url.replace("https://" + appProvider.AGL_TENANT_URL, API_BASE_URL);
    }

    return url;
  }, [appProvider]);

  // retry logic for token fetch failures
  const retryTokenFetch = useCallback(
    (res, isError, skipReactiveRetry = false) => {
      if (shouldRetryRefresh(res?.data?.errorDescription)) {
        // Only retry if not skipped by Service Worker
        if (!skipReactiveRetry) {
          const handler = getTokenRefreshHandler();
          if (handler) {
            handler().catch(() => {});
          }
        }
      } else if (res?.data?.errorDescription === AVS_ERROR_CODES.INVALID_STREAM_SESSION) {
        // Always reload app for INVALID_STREAM_SESSION, regardless of skipReactiveRetry
        reloadApp(appProvider);
      } else if (isError) {
        // Only retry if not skipped by Service Worker
        if (!skipReactiveRetry) {
          const handler = getTokenRefreshHandler();
          if (handler) {
            handler().catch(() => {});
          }
        }
      }
    },
    [appProvider]
  );

  const processResponse = useCallback(
    (jsonData) => {
      if (jsonData?.data) {
        // Store new avs-refresh-token
        if (jsonData.data?.headers?.["avs-refresh-token"]) {
          setCookie(AVS_REFRESH_TOKEN, jsonData.data.headers[AVS_REFRESH_TOKEN], 365);
          setLocalStorage(AVS_REFRESH_TOKEN, jsonData.data.headers[AVS_REFRESH_TOKEN]);
        }
        updateExpiryFromResultObj(jsonData?.data?.resultObj);
        retryTokenFetch(jsonData);
      }
    },
    [retryTokenFetch]
  );

  const processResponseFailure = useCallback(
    (jsonData) => {
      if (jsonData?.data) {
        retryTokenFetch(jsonData, true, jsonData.skipReactiveRetry);
      }
    },
    [retryTokenFetch]
  );

  // Register Service Worker for background keep-alive on login
  useEffect(() => {
    console.log("[SessionExpiryManager] SW registration effect running, isLoggedIn:", userProfile?.isLoggedIn);
    if (userProfile?.isLoggedIn) {
      console.log("[SessionExpiryManager] Attempting to register Service Worker...");
      registerKeepAliveServiceWorker().catch((error) => {
        console.warn("[SessionExpiryManager] Service Worker registration failed:", error);
      });
      const renewUrl = buildRenewUrl();
      const refreshToken = getLocalStorage(AVS_REFRESH_TOKEN);
      const isLegacy = !featureToggles?.isAvsTokenExpirationProvided;
      console.log(
        "[SessionExpiryManager] Sending config to SW, renewUrl:",
        renewUrl ? "present" : "missing",
        "token:",
        refreshToken ? "present" : "missing"
      );

      updateKeepAliveServiceWorkerConfig(processResponse, processResponseFailure, {
        renewUrl,
        refreshToken,
        videoPlaying,
        isLegacy,
      }).catch((error) => {
        console.warn("[SessionExpiryManager] Failed to update Service Worker config:", error);
      });

      if (videoPlaying?.playing && videoPlaying?.currentContentId && videoPlaying?.assetId) {
        // Setup manual keep-alive fallback as backup (10 minute interval)
        console.log("[SessionExpiryManager] Setting up manual SW keep-alive as fallback every 10 minutes");
        if (manualSwKeepAliveCleanup.current) {
          manualSwKeepAliveCleanup.current();
        }
        manualSwKeepAliveCleanup.current = setupManualServiceWorkerKeepAlive(600);
      } else if (manualSwKeepAliveCleanup.current) {
        // Cleanup manual keep-alive on logout
        manualSwKeepAliveCleanup.current();
        manualSwKeepAliveCleanup.current = null;
      }
    } else {
      clearKeepAliveServiceWorkerConfig().catch((error) => {
        console.warn("[SessionExpiryManager] Failed to clear Service Worker config:", error);
      });
      // Cleanup manual keep-alive on logout
      if (manualSwKeepAliveCleanup.current) {
        manualSwKeepAliveCleanup.current();
        manualSwKeepAliveCleanup.current = null;
      }
    }
  }, [
    userProfile,
    buildRenewUrl,
    featureToggles?.isAvsTokenExpirationProvided,
    videoPlaying,
    processResponse,
    processResponseFailure,
  ]);

  return null;
}

SessionExpiryManager.propTypes = {
  appProvider: PropTypes.object,
  userProfile: PropTypes.object,
  videoPlaying: PropTypes.object,
  keepAliveDelay: PropTypes.number,
  featureToggles: PropTypes.object,
};

function mapStateToProps({ app }) {
  return {
    appProvider: app.provider,
    userProfile: app.userProfile,
    videoPlaying: app.videoPlaying,
    featureToggles: app.featureToggles,
  };
}

export default connect(mapStateToProps)(SessionExpiryManager);
